import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { FaTimes } from 'react-icons/fa'; // Icon for removing files
import AlertContext from '../../../../../Context/Alert/AlertContext'; // Adjust the import path as needed
import { BaseURL } from '../../../../../Files/BaseURL'; // Adjust the import path as needed

function UploadDoc() {
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertContext); // Using AlertContext for user feedback

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Validate the number of files
    if (selectedFiles.length + files.length > 5) {
      showAlert('You can only upload up to 5 files.', 'danger');
      return;
    }

    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
  };

  // Handle file removal
  const removeFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      showAlert('Please select at least one file to upload.', 'danger');
      return;
    }

    setIsUploading(true);

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const authToken = localStorage.getItem("p@token"); // Retrieve token from localStorage or context

      const response = await fetch(`${BaseURL}/api/business/companysetup/companybsuinessupload`, {
        method: 'PUT',
        headers: {
          'auth-token': authToken,
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        showAlert(result.message, 'success');
        navigate('/profileCreation/company/plan'); // Navigate to the next step
      } else {
        showAlert(result.message || 'Failed to upload documents.', 'danger');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      showAlert('An error occurred while uploading files. Please try again.', 'danger');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {/* Progress Bar */}
      <div className="bg-gray-100">
        <div className='w-[100%] h-2 bg-primarygreen rounded-r-full'></div>
      </div>

      {/* Upload Container */}
      <div className="flex justify-center items-center bg-gray-100 py-20">
        <div className="mx-6 w-[95%] md:w-[60%] lg:w-[40%] bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-pop text-center font-bold mb-4">Upload Documents</h2>

          {/* Instructions */}
          <div className="mb-4 flex flex-col gap-2">
            <p className="text-black font-light font-pop text-lg mb-2">
              Please upload all relevant documents regarding your skills or business - such as e.g., certificates or licenses.
            </p>

            {/* File Upload Input */}
            <div className="flex items-center justify-center w-full">
              <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full border-2 border-primarygreen border-dashed rounded-lg cursor-pointer bg-transparent hover:bg-gray-100">
                <div className="flex flex-row gap-4 items-center justify-center py-3 text-primarygreen font-pop">
                  <IoIosAddCircleOutline className='text-2xl' />
                  <p className="text-lg">Upload file</p>
                </div>
                <input
                  id="dropzone-file"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 font-pop">
            {/* Display Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">Selected Files:</p>
                <ul className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="flex items-center justify-between bg-gray-100 p-2 rounded">
                      <span className="text-gray-700">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700"
                        aria-label={`Remove ${file.name}`}
                      >
                        <FaTimes />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isUploading}
            className={`my-4 w-full bg-green-500 ${isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-transparent hover:bg-white-500'
              } font-semibold hover:text-black font-pop text-xl py-3 px-4 border border-black-500 hover:border-green-500 rounded-full text-white animate__animated animate__backInUp transition duration-300`}
          >
            {isUploading ? 'Uploading...' : (
              <span>
                Send
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

export default UploadDoc;
